import React, { useMemo, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CSSModules from 'react-css-modules';
import * as PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';

import { selectVisitTestCallPerformed } from '../../../../redux/data/selectors';
import { getAppointmentTime } from '../../utils/appointment';
import {
  APPOINTMENT_STATUSES,
  AW_TYPE_CODE,
  INLINE_TYPES,
  EIR_APPOINTMENT_TYPES,
} from '../../../../constants';
import getVisitTypeName from '../../../../utils/appointments/getVisitTypeName';
import Shape from '../../../common/Shape';
import { useVirtualVisitGuard } from '../../utils/useVirtualVisitGuard';
import { hasProviderProfileSelector } from '../../../../redux/context/selectors';
import { testCallActions } from '../../../../redux/healthboard/test-call';
import styles from './styles.styl';
import TestVideoButton from './components/TestVideoButton';
import VisitButtons from './components/VisitButtons';
import RenderVisitView from './components/RenderVisitView';

function AppointmentCard({
  data: appointment,
  isUpcoming,
  formatMessage,
  onStartVideoVisit,
  onClick,
  providerView,
  providerContext,
}) {
  const dispatch = useDispatch();
  const isProvider = useSelector(hasProviderProfileSelector);
  const testCallPerformed = useSelector(selectVisitTestCallPerformed);

  const {
    showVisitBtn,
    virtualCallEnabled,
    isUpcomingAppointmentStatus,
  } = useVirtualVisitGuard({
    appointment,
    isUpcoming,
  });

  const { hours, date } = useMemo(
    () => getAppointmentTime({ appointment, isUpcoming }),
    [appointment, isUpcoming],
  );

  const code = get(appointment, 'visitType.code');

  const visitTypeName = get(appointment, 'visitType.name');

  const visitType = getVisitTypeName({
    sourceOfCreation: get(appointment, 'sourceOfCreation'),
    defaultValue: visitTypeName,
    formatMessage,
    visitTypeCode: code,
  });

  const formattedDuration = get(appointment, 'formattedDuration');

  const status =
    INLINE_TYPES.includes(code) && isUpcomingAppointmentStatus
      ? formatMessage({ id: 'appointment.list.tabs.inLine' })
      : get(appointment, 'status.coreAppName');

  const isJoinVirtualVisitByCode = get(
    appointment,
    'service.joinVirtualVisitByCode',
  );

  const isConfirmedByPatient = get(appointment, 'confirmed');
  const isConfirmByPatientEnabled = get(appointment, 'canConfirmVisit');
  const prohibitConfirmationForPatientByReasonType = get(
    appointment,
    'reasonType.prohibitConfirmationForPatient',
    false,
  );
  const { checkIn } = get(appointment, 'allowedActions') || {};
  const isAW = code === AW_TYPE_CODE;

  const isPendingStatus = status === APPOINTMENT_STATUSES.PENDING;
  const manageable =
    providerView || providerContext ? get(appointment, 'manageable') : true;

  const isPatientConfirmationAllowed =
    isConfirmByPatientEnabled && !prohibitConfirmationForPatientByReasonType;

  const showConfirmByPatientButton =
    isPatientConfirmationAllowed && !isProvider && !checkIn;

  const showVideoVisitButton = virtualCallEnabled && showVisitBtn;

  const statusTitle = isConfirmedByPatient
    ? `${status}/${formatMessage({ id: 'common.confirmed' })}`
    : status;

  const securedFormattedDuration = useMemo(() => {
    if (
      EIR_APPOINTMENT_TYPES.includes(code) &&
      status !== APPOINTMENT_STATUSES.COMPLETED
    ) {
      return '';
    }

    return formattedDuration ? `(${formattedDuration})` : '';
  }, [formattedDuration]);

  const handleOpenTestCall = e => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(testCallActions.init({ onStartVideoVisit, virtualCallEnabled }));
  };

  const handleStartVideoVisit = e => {
    return testCallPerformed ? onStartVideoVisit() : handleOpenTestCall(e);
  };

  const handleClick = () => {
    if (isAW || !manageable) return;

    onClick();
  };

  return (
    <Fragment>
      <Shape
        className={cn(styles.root, { [styles.notManagable]: !manageable })}
        onClick={handleClick}
      >
        <div styleName="row" className={styles.strong}>
          <span>{`${visitType} ${date}, ${hours} ${securedFormattedDuration}`}</span>
          <div styleName="right">
            <span
              className={cn(styles.status, {
                [styles.pendingStatus]: isPendingStatus,
              })}
            >
              {statusTitle}
            </span>
          </div>
        </div>
        <div styleName="separator" />
        <div styleName="content">
          <RenderVisitView
            appointment={appointment}
            providerContext={providerContext}
            providerView={providerView}
          />
          {manageable &&
          (virtualCallEnabled || isPatientConfirmationAllowed) ? (
            <VisitButtons
              appointment={appointment}
              isJoinVirtualVisitByCode={isJoinVirtualVisitByCode}
              handleStartVideoVisit={handleStartVideoVisit}
              showConfirmByPatientButton={showConfirmByPatientButton}
              showVideoVisitButton={showVideoVisitButton}
            />
          ) : (
            <TestVideoButton
              handleOpenTestCall={handleOpenTestCall}
              isJoinVirtualVisitByCode={isJoinVirtualVisitByCode}
              showVisitBtn={showVisitBtn}
            />
          )}
        </div>
      </Shape>
    </Fragment>
  );
}

AppointmentCard.propTypes = {
  data: PropTypes.object,
  onClick: PropTypes.func,
  onStartVideoVisit: PropTypes.func,
  formatMessage: PropTypes.func,
  providerContext: PropTypes.bool,
  providerView: PropTypes.bool,
  isUpcoming: PropTypes.bool,
};

export default CSSModules(AppointmentCard, styles, { allowMultiple: true });



// WEBPACK FOOTER //
// ./src/components/Healthboard/Components/AppointmentCard/index.jsx